<template>
  <div class="home">
    <div class="home-toolbar">
      <MonthYearFilter
        v-model="selectedPeriod"
        label="Visão geral por período"
      />

      <button type="button" class="visibility-btn" @click="toggleValuesVisibility">
        <i :class="hideValues ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
        <span>{{ hideValues ? 'Mostrar valores' : 'Ocultar valores' }}</span>
      </button>
    </div>

    <section class="stats-grid">
      <article class="stat-card">
        <div class="stat-header">
          <span class="stat-label">Parcelas Pendentes</span>
          <i class="fas fa-clock"></i>
        </div>
        <p class="stat-number">{{ honorariosPendentes.length }}</p>
        <p class="stat-value" :class="{ blurred: hideValues }">{{ formatMoney(totalPendente) }}</p>
      </article>

      <article class="stat-card">
        <div class="stat-header">
          <span class="stat-label">Parcelas Pagas</span>
          <i class="fas fa-check-circle"></i>
        </div>
        <p class="stat-number">{{ honorariosPagos.length }}</p>
        <p class="stat-value" :class="{ blurred: hideValues }">{{ formatMoney(totalRecebido) }}</p>
      </article>

      <article class="stat-card">
        <div class="stat-header">
          <span class="stat-label">Total de Clientes</span>
          <i class="fas fa-users"></i>
        </div>
        <p class="stat-number">{{ totalClientes }}</p>
        <p class="stat-value muted">Carteira ativa</p>
      </article>

      <article class="stat-card">
        <div class="stat-header">
          <span class="stat-label">Total Geral</span>
          <i class="fas fa-chart-line"></i>
        </div>
        <p class="stat-number" :class="{ blurred: hideValues }">{{ formatMoney(totalGeral) }}</p>
        <p class="stat-value muted">{{ selectedPeriodLabel }}</p>
      </article>
    </section>

    <section class="panels-grid">
      <article class="panel-card">
        <div class="panel-head">
          <h3>Status das Parcelas</h3>
          <span class="panel-caption">{{ selectedPeriodLabel }}</span>
        </div>
        <div class="bar-track">
          <div class="bar-segment pending" :style="{ width: pendingPercentage + '%' }"></div>
          <div class="bar-segment paid" :style="{ width: paidPercentage + '%' }"></div>
        </div>
        <div class="legend-row">
          <span><i class="dot pending"></i>Pendentes: {{ honorariosPendentes.length }}</span>
          <span><i class="dot paid"></i>Pagos: {{ honorariosPagos.length }}</span>
        </div>
      </article>

      <article class="panel-card">
        <div class="panel-head">
          <h3>Distribuição de Valores</h3>
          <span class="panel-caption">Por vencimento</span>
        </div>
        <div class="metric-row">
          <span>Pendente</span>
          <strong :class="{ blurred: hideValues }">{{ formatMoney(totalPendente) }}</strong>
        </div>
        <div class="meter">
          <div class="meter-fill pending" :style="{ width: pendingValuePercentage + '%' }"></div>
        </div>

        <div class="metric-row">
          <span>Recebido</span>
          <strong :class="{ blurred: hideValues }">{{ formatMoney(totalRecebido) }}</strong>
        </div>
        <div class="meter">
          <div class="meter-fill paid" :style="{ width: paidValuePercentage + '%' }"></div>
        </div>
      </article>
    </section>

    <section class="insights-grid">
      <InsightListCard
        title="Clientes com maior atraso"
        description="Ordenado pelo maior saldo em atraso. Considera o vencimento de cada parcela."
        :is-empty="!clientesEmAtraso.length"
        empty-text="Nenhum cliente com parcela em atraso neste periodo."
      >
        <div class="list-grid">
          <article v-for="cliente in clientesEmAtraso" :key="cliente.customerId" class="list-row">
            <div>
              <p class="row-title">{{ cliente.customerName }}</p>
              <p class="row-subtitle">{{ cliente.overdueInstallments }} parcela(s) em atraso</p>
            </div>
            <strong class="row-value negative" :class="{ blurred: hideValues }">{{ formatMoney(cliente.totalOverdue) }}</strong>
          </article>
        </div>
      </InsightListCard>

      <InsightListCard
        title="Maiores clientes"
        description="Ranking por valor total ja recebido no periodo selecionado."
        :is-empty="!maioresClientes.length"
        empty-text="Nenhum pagamento registrado para montar o ranking."
      >
        <div class="list-grid">
          <article v-for="cliente in maioresClientes" :key="cliente.customerId" class="list-row">
            <div>
              <p class="row-title">{{ cliente.customerName }}</p>
              <p class="row-subtitle">{{ cliente.receivedCount }} parcela(s) recebidas</p>
            </div>
            <strong class="row-value" :class="{ blurred: hideValues }">{{ formatMoney(cliente.totalPaid) }}</strong>
          </article>
        </div>
      </InsightListCard>

      <InsightListCard
        title="Parcelas prestes a vencer"
        :description="`Parcelas pendentes com vencimento nos proximos ${daysAhead} dias.`"
        :is-empty="!honorariosPrestesAVencer.length"
        empty-text="Nenhuma parcela prestes a vencer neste recorte."
      >
        <template #actions>
          <label class="days-filter">
            <span>Janela</span>
            <input v-model.number="daysAhead" type="number" min="1" max="60" class="days-input">
          </label>
        </template>

        <div class="list-grid">
          <article v-for="honorario in honorariosPrestesAVencer" :key="honorario.id" class="list-row">
            <div>
              <p class="row-title">{{ honorario.customerName }}</p>
              <p class="row-subtitle">Vence em {{ honorario.dueDateLabel }}</p>
            </div>
            <strong class="row-value" :class="{ blurred: hideValues }">{{ formatMoney(honorario.value) }}</strong>
          </article>
        </div>
      </InsightListCard>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import type { Customer, Honorary } from '@/types'
import MonthYearFilter from '@/components/dashboard/MonthYearFilter.vue'
import InsightListCard from '@/components/dashboard/InsightListCard.vue'
import { expandHonoraryInstallments, type HonoraryInstallment } from '@/utils/installments'

type OverdueClient = {
  customerId: string
  customerName: string
  totalOverdue: number
  overdueInstallments: number
}

type TopClient = {
  customerId: string
  customerName: string
  totalPaid: number
  receivedCount: number
}

type UpcomingHonorary = {
  id: string
  customerName: string
  value: number
  dueDateLabel: string
  dueDateTimestamp: number
}

type DashboardInstallment = HonoraryInstallment & {
  customerName: string
  dueDateLabel: string
  dueDateTimestamp: number
}

export default defineComponent({
  name: 'Home',
  components: {
    MonthYearFilter,
    InsightListCard
  },
  setup() {
    const store = useStore()
    const selectedPeriod = ref('')
    const daysAhead = ref(7)
    const hideValues = ref(false)

    const honorarios = computed<Honorary[]>(() => store.state.honorarios as Honorary[])
    const clientes = computed<Customer[]>(() => store.state.clientes as Customer[])
    const totalClientes = computed(() => store.state.clientes.length)

    const parseHonoraryDate = (dateValue: string | Date) => {
      const parsedDate = new Date(dateValue)
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
    }

    const startOfDay = (date: Date) => {
      const normalizedDate = new Date(date)
      normalizedDate.setHours(0, 0, 0, 0)
      return normalizedDate
    }

    const selectedPeriodLabel = computed(() => {
      if (!selectedPeriod.value) {
        return 'Visão Geral'
      }

      const [year, month] = selectedPeriod.value.split('-')

      if (year && !month) {
        return `Ano de ${year}`
      }

      const parsedDate = new Date(Number(year), Number(month) - 1, 1)
      return parsedDate.toLocaleDateString('pt-BR', {
        month: 'long',
        year: 'numeric'
      })
    })

    const getCustomerName = (customerId: string) => {
      return clientes.value.find((cliente) => cliente.id === customerId)?.fullName || 'Cliente nao identificado'
    }

    const formatMoney = (value: number) => `R$ ${value.toFixed(2)}`

    const toggleValuesVisibility = () => {
      hideValues.value = !hideValues.value
    }

    const installments = computed<DashboardInstallment[]>(() =>
      honorarios.value.flatMap((honorario) =>
        expandHonoraryInstallments(honorario).map((installment) => {
          const parsedDueDate = parseHonoraryDate(installment.dueDate) || new Date()

          return {
            ...installment,
            customerName: getCustomerName(honorario.customerId),
            dueDateLabel: parsedDueDate.toLocaleDateString('pt-BR'),
            dueDateTimestamp: startOfDay(parsedDueDate).getTime()
          }
        })
      )
    )

    const filteredInstallments = computed(() => {
      if (!selectedPeriod.value) {
        return installments.value
      }

      const [yearPart, monthPart] = selectedPeriod.value.split('-')
      const year = Number(yearPart)
      const month = monthPart ? Number(monthPart) : null

      return installments.value.filter((installment) => {
        const parsedDate = parseHonoraryDate(installment.dueDate)

        if (!parsedDate || parsedDate.getFullYear() !== year) {
          return false
        }

        if (!month) {
          return true
        }

        return parsedDate.getMonth() + 1 === month
      })
    })

    const honorariosPendentes = computed(() =>
      filteredInstallments.value.filter((installment) => installment.status === 'Pendente')
    )

    const honorariosPagos = computed(() =>
      filteredInstallments.value.filter((installment) => installment.status === 'Recebido')
    )

    const totalPendente = computed(() =>
      honorariosPendentes.value.reduce((total, installment) => total + Math.max(installment.value - installment.paidValue, 0), 0)
    )

    const totalRecebido = computed(() =>
      honorariosPagos.value.reduce((total, installment) => total + installment.paidValue, 0)
    )

    const totalGeral = computed(() => totalPendente.value + totalRecebido.value)
    const totalHonorarios = computed(() => filteredInstallments.value.length)

    const pendingPercentage = computed(() =>
      totalHonorarios.value > 0 ? (honorariosPendentes.value.length / totalHonorarios.value) * 100 : 0
    )

    const paidPercentage = computed(() =>
      totalHonorarios.value > 0 ? (honorariosPagos.value.length / totalHonorarios.value) * 100 : 0
    )

    const pendingValuePercentage = computed(() =>
      totalGeral.value > 0 ? (totalPendente.value / totalGeral.value) * 100 : 0
    )

    const paidValuePercentage = computed(() =>
      totalGeral.value > 0 ? (totalRecebido.value / totalGeral.value) * 100 : 0
    )

    const clientesEmAtraso = computed<OverdueClient[]>(() => {
      const today = startOfDay(new Date())
      const grouped: Record<string, OverdueClient> = {}

      honorariosPendentes.value.forEach((installment) => {
        if (installment.dueDateTimestamp >= today.getTime()) {
          return
        }

        if (!grouped[installment.customerId]) {
          grouped[installment.customerId] = {
            customerId: installment.customerId,
            customerName: installment.customerName,
            totalOverdue: 0,
            overdueInstallments: 0
          }
        }

        grouped[installment.customerId].totalOverdue += Math.max(installment.value - installment.paidValue, 0)
        grouped[installment.customerId].overdueInstallments += 1
      })

      return Object.values(grouped)
        .sort((a, b) => b.totalOverdue - a.totalOverdue)
        .slice(0, 8)
    })

    const maioresClientes = computed<TopClient[]>(() => {
      const grouped: Record<string, TopClient> = {}

      honorariosPagos.value.forEach((installment) => {
        if (!grouped[installment.customerId]) {
          grouped[installment.customerId] = {
            customerId: installment.customerId,
            customerName: installment.customerName,
            totalPaid: 0,
            receivedCount: 0
          }
        }

        grouped[installment.customerId].totalPaid += installment.paidValue
        grouped[installment.customerId].receivedCount += 1
      })

      return Object.values(grouped)
        .sort((a, b) => b.totalPaid - a.totalPaid)
        .slice(0, 8)
    })

    const honorariosPrestesAVencer = computed<UpcomingHonorary[]>(() => {
      const today = startOfDay(new Date())
      const futureLimit = startOfDay(new Date())
      futureLimit.setDate(futureLimit.getDate() + Math.max(daysAhead.value, 1))

      return honorariosPendentes.value
        .map((installment) => {
          if (installment.dueDateTimestamp < today.getTime() || installment.dueDateTimestamp > futureLimit.getTime()) {
            return null
          }

          return {
            id: installment.id,
            customerName: installment.customerName,
            value: Math.max(installment.value - installment.paidValue, 0),
            dueDateLabel: installment.dueDateLabel,
            dueDateTimestamp: installment.dueDateTimestamp
          }
        })
        .filter((item): item is UpcomingHonorary => item !== null)
        .sort((a, b) => a.dueDateTimestamp - b.dueDateTimestamp)
        .slice(0, 8)
    })

    onMounted(async () => {
      if (!store.state.clientes.length) {
        await store.dispatch('loadClientes')
      }

      if (!store.state.honorarios.length) {
        await store.dispatch('loadHonorarios')
      }

      // Mock de referencia para API:
      // [
      //   { customerId: '1', customerName: 'Cliente A', totalOverdue: 2300, overdueInstallments: 2 },
      //   { customerId: '2', customerName: 'Cliente B', totalPaid: 8900 },
      //   { id: 'h1', customerName: 'Cliente C', value: 1500, dueDateLabel: '05/04/2026' }
      // ]
    })

    return {
      selectedPeriod,
      selectedPeriodLabel,
      daysAhead,
      hideValues,
      honorariosPendentes,
      honorariosPagos,
      totalPendente,
      totalRecebido,
      totalClientes,
      totalGeral,
      pendingPercentage,
      paidPercentage,
      pendingValuePercentage,
      paidValuePercentage,
      clientesEmAtraso,
      maioresClientes,
      honorariosPrestesAVencer,
      formatMoney,
      toggleValuesVisibility
    }
  }
})
</script>

<style scoped>
.home {
  display: grid;
  gap: 16px;
}

.home-toolbar {
  display: grid;
  gap: 12px;
}

.visibility-btn {
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1px solid color-mix(in srgb, var(--border-color) 82%, transparent);
  border-radius: 12px;
  background: color-mix(in srgb, var(--bg-secondary) 88%, transparent);
  color: var(--text-primary);
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.visibility-btn:hover {
  background: color-mix(in srgb, var(--bg-primary) 78%, transparent);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}

.stat-card {
  padding: 18px;
  border-radius: 16px;
  background: color-mix(in srgb, var(--bg-secondary) 84%, transparent);
  border: 1px solid color-mix(in srgb, var(--border-color) 82%, transparent);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
}

.stat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  color: var(--text-secondary);
}

.stat-label {
  font-size: 12px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.stat-number {
  font-size: 30px;
  line-height: 1.2;
  color: var(--text-primary);
  font-weight: 600;
}

.stat-value {
  margin-top: 4px;
  color: #7fc3ff;
  font-size: 14px;
  font-weight: 500;
}

.stat-value.muted {
  color: var(--text-secondary);
  text-transform: capitalize;
}

.panels-grid,
.insights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 14px;
}

.panel-card {
  padding: 18px;
  border-radius: 16px;
  background: color-mix(in srgb, var(--bg-secondary) 84%, transparent);
  border: 1px solid color-mix(in srgb, var(--border-color) 82%, transparent);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
}

.panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.panel-head h3 {
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
}

.panel-caption {
  color: var(--text-secondary);
  font-size: 12px;
  text-align: right;
  text-transform: capitalize;
}

.bar-track {
  height: 14px;
  border-radius: 999px;
  overflow: hidden;
  background: color-mix(in srgb, var(--bg-primary) 72%, transparent);
  display: flex;
  margin-bottom: 12px;
}

.bar-segment {
  height: 100%;
  transition: width 0.3s ease;
}

.bar-segment.pending {
  background: linear-gradient(90deg, #ffb75d, #ff9f43);
}

.bar-segment.paid {
  background: linear-gradient(90deg, #34d399, #10b981);
}

.legend-row {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 13px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
}

.dot.pending {
  background: #ffb75d;
}

.dot.paid {
  background: #34d399;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  color: var(--text-secondary);
  margin-bottom: 6px;
  font-size: 14px;
}

.metric-row strong {
  color: var(--text-primary);
  font-weight: 600;
}

.meter {
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: color-mix(in srgb, var(--bg-primary) 72%, transparent);
  margin-bottom: 14px;
}

.meter-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.meter-fill.pending {
  background: linear-gradient(90deg, #ffb75d, #ff9f43);
}

.meter-fill.paid {
  background: linear-gradient(90deg, #34d399, #10b981);
}

.list-grid {
  display: grid;
  gap: 10px;
}

.list-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--bg-primary) 70%, transparent);
  border: 1px solid color-mix(in srgb, var(--border-color) 70%, transparent);
}

.row-title {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
}

.row-subtitle {
  margin-top: 4px;
  color: var(--text-secondary);
  font-size: 12px;
}

.row-value {
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 700;
  text-align: right;
}

.row-value.negative {
  color: #ff9f43;
}


.blurred {
  filter: blur(7px);
  user-select: none;
}
.days-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 12px;
}

.days-input {
  width: 72px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--border-color) 82%, transparent);
  background: color-mix(in srgb, var(--bg-primary) 80%, transparent);
  color: var(--text-primary);
}

@media (max-width: 640px) {
  .visibility-btn {
    width: 100%;
    justify-content: center;
  }

  .stats-grid,
  .panels-grid,
  .insights-grid {
    grid-template-columns: 1fr;
  }

  .stat-card,
  .panel-card {
    padding: 16px;
  }

  .stat-number {
    font-size: 26px;
  }

  .list-row,
  .panel-head,
  .days-filter {
    align-items: flex-start;
    flex-direction: column;
  }

  .row-value {
    text-align: left;
  }
}
</style>


